import React, { useRef, useCallback, useEffect } from 'react';
import { Button } from 'semantic-ui-react';
import './OcrImagePickerModal.scss';
import OcrImageDisplayModal from './OcrImageDisplayModal';
import OcrImageResultModal from './OcrImageResultModal';
import OcrCamera from './OcrCamera';
import OcrDropZone from './OcrDropZone';
import { apimCall, publicShareApimCall } from '../../../helpers/api';
import { GoFormzServices } from '../../../AppSettings';
import { showMessage } from '../../../helpers/MessageUtility';
import CameraIcon from '../../../icons/CameraIcon';
import ImageGallery from '../../../icons/ImageGallery';
import { OCR_CONSTANTS, OCR_MESSAGES } from './constants';
import { normalizeOcrResults } from './utils';
import AiMagic from '../../../icons/AiMagic';
import { useImmerState } from '../../../hooks/useImmerState';
import { useAppSelector } from '../../../hooks/ReduxHooks';

interface OcrImagePickerState {
  currentView: 'dropzone' | 'camera';
  capturedImage: string | null;
  originalFile: File | null;
  isProcessing: boolean;
  showImageDisplay: boolean;
  showResultsModal: boolean;
  ocrResults: string[];
  isFromCamera: boolean;
}

const DEFAULT_STATE: OcrImagePickerState = {
  currentView: 'dropzone',
  capturedImage: null,
  originalFile: null,
  isProcessing: false,
  showImageDisplay: false,
  showResultsModal: false,
  ocrResults: [],
  isFromCamera: false
};

export interface OcrImagePickerModalProps {
  onClose: () => void;
  maxFileSizeInMB?: number;
  externalImageSrc?: string | null;
  externalImageFile?: File | null;
  startWithDisplay?: boolean;
  onOcrResults?: (results: string[]) => void;
}

export default function OcrImagePickerModal({
  onClose,
  maxFileSizeInMB = OCR_CONSTANTS.MAX_FILE_SIZE_MB,
  externalImageSrc,
  externalImageFile,
  startWithDisplay = false,
  onOcrResults
}: OcrImagePickerModalProps) {
  const [state, updateState] = useImmerState(DEFAULT_STATE);
  const { currentView, capturedImage, originalFile, isProcessing, showImageDisplay, showResultsModal, ocrResults, isFromCamera } = state;
  const sharedAccessToken = useAppSelector((state) => state.formeditor?.sharedAccessToken);

  const cameraStopRef = useRef<(() => void) | null>(null);

  // Initialize with external image if provided
  useEffect(() => {
    if (externalImageSrc && externalImageFile && startWithDisplay) {
      updateState((draft) => {
        draft.capturedImage = externalImageSrc;
        draft.originalFile = externalImageFile;
        draft.showImageDisplay = true;
      });
    }
  }, [externalImageSrc, externalImageFile, startWithDisplay, updateState]);

  const handleCameraImageCaptured = useCallback(
    (imageSrc: string, file: File) => {
      updateState((draft) => {
        draft.capturedImage = imageSrc;
        draft.originalFile = file;
        draft.isFromCamera = true;
        draft.showImageDisplay = true;
      });
    },
    [updateState]
  );

  const handleCameraReady = useCallback((stopCamera: () => void) => {
    cameraStopRef.current = stopCamera;
  }, []);

  const handleCloseModal = useCallback(() => {
    if (cameraStopRef.current) {
      cameraStopRef.current();
      cameraStopRef.current = null;
    }
    // Clear all image state when closing the modal
    updateState((draft) => {
      draft.capturedImage = null;
      draft.originalFile = null;
      draft.ocrResults = [];
      draft.showImageDisplay = false;
      draft.showResultsModal = false;
    });
    onClose();
  }, [onClose, updateState]);

  const handleFinalUseImage = useCallback(async () => {
    if (originalFile) {
      try {
        updateState((draft) => {
          draft.isProcessing = true;
        });
        const formData = new FormData();
        formData.append('image', originalFile);

        const response = sharedAccessToken
          ? await publicShareApimCall('/ai/vision', 'POST', formData, null, null, null, true)
          : await apimCall(GoFormzServices.AIService, '/ai/vision', 'POST', formData, null, null, 'json', true);

        const textResults = normalizeOcrResults(response);
        updateState((draft) => {
          draft.ocrResults = textResults;
          draft.showImageDisplay = false;
          draft.showResultsModal = true;
        });

        onOcrResults?.(textResults);
      } catch (error: any) {
        // Check for 429 status code (rate limit exceeded)
        if (error?.response?.status === 429 || error?.status === 429) {
          showMessage('error', OCR_MESSAGES.ACCOUNT_LIMIT_REACHED);
        } else {
          showMessage('error', OCR_MESSAGES.UPLOAD_ERROR);
        }
        handleCloseModal();
      } finally {
        updateState((draft) => {
          draft.isProcessing = false;
        });
      }
    }
  }, [originalFile, handleCloseModal, onOcrResults, updateState]);

  const switchToDropzone = useCallback(() => {
    if (cameraStopRef.current) {
      cameraStopRef.current();
      cameraStopRef.current = null;
    }
    updateState((draft) => {
      draft.currentView = 'dropzone';
    });
  }, [updateState]);

  const switchToCamera = useCallback(() => {
    updateState((draft) => {
      draft.currentView = 'camera';
    });
  }, [updateState]);

  const handleDropzoneImageSelected = useCallback(
    (imageSrc: string, file: File) => {
      updateState((draft) => {
        draft.capturedImage = imageSrc;
        draft.originalFile = file;
        draft.isFromCamera = false;
        draft.showImageDisplay = true;
      });
    },
    [updateState]
  );

  const handleBackToImagePicker = useCallback(() => {
    // Clear the captured image and original file to reset to picker state
    updateState((draft) => {
      draft.capturedImage = null;
      draft.originalFile = null;
      draft.showImageDisplay = false;
      draft.showResultsModal = false;
      draft.isFromCamera = false;
    });
  }, [updateState]);

  return (
    <>
      {!showImageDisplay && !showResultsModal ? (
        <div className="ocr-panel">
          <div className="ocr-header">
            <div className="ocr-header-beta">
              <AiMagic large={true} />
              BETA
              <span className="ocr-header-beta-span"> - Pull text from image</span>
            </div>
            <Button className="ocr-close-button" onClick={handleCloseModal}>
              Close
            </Button>
          </div>
          <div className="ocr-content">
            <div className="ocr-btn-row">
              <div className="ocr-btn-wrapper">
                <Button
                  icon={<ImageGallery />}
                  size="large"
                  className={`ocr-btn-select-source ${currentView === 'dropzone' ? 'selected' : ''}`}
                  onClick={switchToDropzone}
                />
                <div className="ocr-btn-label">Image</div>
              </div>
              <div className="ocr-btn-wrapper">
                <Button
                  icon={<CameraIcon />}
                  size="large"
                  className={`ocr-btn-select-source ${currentView === 'camera' ? 'selected' : ''}`}
                  onClick={switchToCamera}
                />
                <div className="ocr-btn-label">Camera</div>
              </div>
            </div>
            {currentView === 'dropzone' && (
              <OcrDropZone maxFileSizeInMB={maxFileSizeInMB} isProcessing={isProcessing} onImageSelected={handleDropzoneImageSelected} />
            )}
            {currentView === 'camera' && <OcrCamera onImageCaptured={handleCameraImageCaptured} onCameraReady={handleCameraReady} />}
          </div>
        </div>
      ) : showImageDisplay ? (
        <OcrImageDisplayModal
          imageSrc={capturedImage}
          onClose={handleCloseModal}
          onBack={handleBackToImagePicker}
          onUse={handleFinalUseImage}
          isFromCamera={isFromCamera}
        />
      ) : (
        <OcrImageResultModal imageSrc={capturedImage} ocrResults={ocrResults} onClose={handleCloseModal} />
      )}
    </>
  );
}
